import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { ModalSize, ModalVariant } from '@/features/configuration-management/location/table/Column';
import { useDynamicLocale } from '@/hoslog-intl';
import { EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { Advertisement } from '@/services/promos-offers/promos-offers.types';
import { ReactNode } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { AdvertiseManagement } from '../../AdvertiseManagement';
type OpenChargesProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;
type onOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string;
  component: ReactNode;
  cancelText?: string;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;
export function getAdvertisementColumns(
  onOpen: onOpenProps,
  openConfirmation: OpenChargesProps,
  t: ReturnType<typeof useDynamicLocale>['t'],
): ColumnDef<Advertisement>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title="S.N" />,
      cell: ({ row, table }) => <div className="font-medium">{getSerialNumber(table, row.index)}</div>,
      enableSorting: false,
    },
    {
      accessorKey: 'title',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => <div>{row.getValue('title')}</div>,
      meta: { label: 'Title' },
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.description')} />
      ),
      cell: ({ row }) => (
        <div className="truncate max-w-[30rem] text-sm text-foreground-detail">{row.getValue('description')}</div>
      ),
      meta: { label: 'Description' },
    },
    {
      accessorKey: 'link',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('adManagement.page.ads.form.link')} />,
      cell: ({ row }) => <div className="text-external-link underline">{row.getValue('link')}</div>,
      meta: { label: 'Link' },
    },
    {
      accessorKey: 'image',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.image')} />,
      cell: ({ row }) => {
        const imageUrl = row.original.image;
        return (
          <div>
            <img src={imageUrl} alt="Advertisement" className="h-full w-full object-cover" />
          </div>
        );
      },
      meta: { label: 'Image' },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption">
            {row.getValue('status') === 'Active' ? (
              <div className="text-success">Active</div>
            ) : (
              <div className="text-destructive-text">Blocked</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'createdDate',
      enableColumnFilter: true,
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        const raw = row.getValue('createdDate');
        return <div>{new Date(raw as string).toLocaleString()}</div>;
      },
      meta: { label: 'Created On' },
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('common.page.tableColumn.action')}
          className="whitespace-nowrap"
        />
      ),
      cell: ({ row }) => (
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => {
              onOpen({
                modalTitle: 'common.page.modal.manageAdvertisement.title',
                title: 'Manage Advertisement',
                okText: t('common.page.modal.submit'),
                size: 'lg',
                component: <AdvertiseManagement />,
              });
            }}
            className="cursor-pointer text-foreground-soft hover:text-foreground-label"
          >
            <EditIcon size={16} />
          </button>
          <Button
            variant={'ghost'}
            className="p-0 m-0"
            onClick={() => {
              openConfirmation({
                title: t('common.page.modal.confirmation.title'),
                description:
                  row.getValue('status') === 'Active'
                    ? t('common.page.modal.blockMessage')
                    : t('common.page.modal.unblockMessage'),
                cancelLabel: t('common.page.modal.no'),
                actionLabel: t('common.page.modal.confirm'),
                onAction: async () => {},
                onCancel: () => {},
                size: 'lg',
                variant: 'warning',
              });
            }}
          >
            {row.getValue('status') === 'Active' ? (
              <LockCloseIcon className="text-destructive-text size-4" />
            ) : (
              <LockOpenIcon className="text-link size-4" />
            )}
          </Button>
        </div>
      ),
    },
  ];
}
